import styles from './InfiniteTable.module.scss';
import OptionPanel from './option-panel/OptionPanel';
import { SORT } from './utils/constants';
import { getHeaderTextByProp } from './utils/helpers';
import headerArrowUp from 'assets/images/header-arrow-up.svg';
import axios from 'axios';
import { SCENARIO_BASE_URL, BASE_URL } from 'constants/api';
import { useMainFilterResetListener } from 'events/resetAllFilters';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { useEffect, useState, useRef } from 'react';
import { filterObjectNonNull, currencyFormatter } from 'utils/helpers';
import ClearFilters from 'components/filters-bar/ClearFilters';
import { TYPES } from './utils/constants';

const InfiniteTable = ({
    zone,
    market,
    team,
    rm,
    start,
    end,
    allValues,
    columns,
    scenario,
    topCss = {}
}) => {
    const tableRef = useRef();
    const tableHeadRef = useRef();
    const tableBodyRef = useRef();
    const [tableData, setTableData] = useState([]);
    const [dataRetrievedLastPage, setDataRetrievedLastPage] = useState(-1);

    const [panelStyles, setPanelStyles] = useState({ top: 0, left: 0 });
    const [isPanelVisible, setIsPanelVisible] = useState(false);
    const [clickedHeader, setClickedHeader] = useState({});
    const [sortedColumns, setSortedColumns] = useState([]);
    const [filteredColumns, setFilteredColumns] = useState([]);

    const PAGE_SIZE = 1000;
    const PAGE_CUT_OFF = 300;
    const OP_PANEL_WIDTH = 156;

    useEffect(() => {
        if ([market, team, rm, start, end].every((e) => e !== null)) {
            clearAllFilters(true);
            getData(0, false);
        }
    }, [market, team, rm, start, end, scenario]);

    useMainFilterResetListener(({ reset }) => {
        if (reset) {
            clearAllFilters(true);
        }
    });

    const getNextPageNumber = () => {
        // Zero index paging.
        return tableData.length / PAGE_SIZE;
    };

    const onTableScroll = () => {
        if (!tableBodyRef.current) {
            return;
        }

        const position = tableBodyRef.current.scrollTop;
        const height =
            document.querySelector(`.${styles.wrapper} tbody tr`).scrollHeight +
            1; // Border

        const currentTopItem = parseInt(position / height);

        if (
            tableData.length >= PAGE_SIZE &&
            currentTopItem > tableData.length - PAGE_CUT_OFF &&
            currentTopItem < tableData.length
        ) {
            const page = getNextPageNumber();
            if (page % 1 === 0) {
                getData(page, true, {
                    sortList: sortedColumns,
                    filterList: filteredColumns
                });
            }
        }
    };

    const scrollTableToTop = () => {
        if (tableBodyRef.current) {
            tableBodyRef.current.scrollTop = 0;
        }
    };

    const generateRequestPayload = (page, options = {}) => {
        return {
            page,
            tableFilters: options.filterList || [],
            sort: options.sortList || []
        };
    };

    const generateApiUrl = (scenario, params) => {
        const baseUrl = scenario ? `${SCENARIO_BASE_URL}${scenario}` : BASE_URL;
        return `${baseUrl}/detailed-view?${queryString.stringify(params)}`;
    };

    const getData = async (pageNumber, isScrolling, sortFilterOptions) => {
        if (isScrolling && dataRetrievedLastPage === pageNumber) {
            return null;
        }
        setDataRetrievedLastPage(pageNumber);

        if (pageNumber === 0) {
            scrollTableToTop();
        }
        const params = filterObjectNonNull({
            rm_zone: typeof zone === 'number' ? zone : null,
            rm_region: market === allValues.market.value ? null : market,
            rm_group: team === allValues.team.value ? null : team,
            rm: rm === allValues.rm.value ? null : rm,
            start: start.epoch,
            end: end.epoch
        });

        const url = generateApiUrl(scenario, params);

        const payload = generateRequestPayload(pageNumber, sortFilterOptions);
        const response = await axios.post(url, payload);

        const { detailedView } = response.data;

        /**
         * If scrolling is false it's a upper level filter change,
         * so no need to append the rows.
         */
        if (isScrolling) {
            Array.prototype.push.apply(tableData, detailedView);
            setTableData([...tableData]);
        } else {
            setTableData([...detailedView]);
        }
    };

    const onHeaderClick = (event, header) => {
        const thWidth = event.currentTarget.clientWidth;
        const left = event.currentTarget.offsetLeft;
        const right = left - (OP_PANEL_WIDTH - thWidth);
        let position = left;

        // This is to avoid panel overflow on screen's right end.
        // First half of columns will align to left and second
        // half panel will align to right.
        const bodyHalfWidth = document.body.clientWidth / 2;
        if (left > bodyHalfWidth) {
            position = right;
        }

        setPanelStyles({
            left: position
        });

        if (header.th === clickedHeader.th) {
            setIsPanelVisible((v) => {
                if (v) {
                    // Clear header selection if toggling visibility
                    // hides the option panel.
                    setClickedHeader({});
                }
                return !v;
            });
        } else {
            setIsPanelVisible(true);
        }
        setClickedHeader(header);
    };

    const updateSortFilters = (sort, filter) => {
        let sortList;
        let filterList;

        if (sort) {
            let columnName = sort.columnName;
            let priorty = sortedColumns.length;
            let index = sortedColumns.findIndex(
                (s) => s.columnName === columnName
            );

            if (index > -1) {
                sortedColumns.splice(index, 1);
            }

            sortList = [...sortedColumns, { ...sort, sortPriority: priorty }];
            setSortedColumns(sortList);
        }
        // Since we are not sending anything if user didn't select any sorting,
        // we need to set available sorting list.
        else {
            sortList = sortedColumns;
        }

        if (filter) {
            let columnName = filter.columnName;
            let index = filteredColumns.findIndex(
                (f) => f.columnName === columnName
            );

            if (index > -1) {
                filteredColumns.splice(index, 1);
            }

            // If user removed search text value and apply filter,
            // this condition will handle not adding removed filter.
            if (!filter.filterValue && filter.filterValue !== 0) {
                filterList = [...filteredColumns];
            } else {
                filterList = [...filteredColumns, filter];
            }

            setFilteredColumns(filterList);
        } else {
            filterList = filteredColumns;
        }

        setIsPanelVisible(false);
        setClickedHeader({});

        if (sort || filter) {
            getData(0, false, { sortList, filterList });
        }
    };

    const renderSortArrow = (header) => {
        const columnName = getHeaderTextByProp(header.prop);
        const sortData = sortedColumns.find((s) => s.columnName === columnName);

        if (sortData) {
            return sortData.sortDirection === SORT.asc ? (
                <img alt="" className={styles.up} src={headerArrowUp} />
            ) : (
                <img alt="" className={styles.down} src={headerArrowUp} />
            );
        }
        return null;
    };

    const renderHeaderText = (header) => {
        const columnName = getHeaderTextByProp(header.prop);
        const hasFilter = filteredColumns
            .map((f) => f.columnName)
            .includes(columnName);
        const hasSort = sortedColumns
            .map((s) => s.columnName)
            .includes(columnName);

        return (
            <span
                className={`${hasFilter ? styles.searched : ''} ${
                    styles.headerText
                } ${hasSort || hasFilter ? styles.selected : ''}`}
            >
                {header.th}
            </span>
        );
    };

    const getHeaderData = () => {
        const columnName = getHeaderTextByProp(clickedHeader.prop);
        const filterData = filteredColumns.find(
            (f) => f.columnName === columnName
        );
        const sortData = sortedColumns.find((s) => s.columnName === columnName);

        return {
            ...clickedHeader,
            filterData,
            sortData
        };
    };

    const clearColumnFilter = (columnName) => {
        let filterList = [...filteredColumns];

        let filterColIndex = filterList.findIndex(
            (f) => f.columnName === columnName
        );
        if (filterColIndex > -1) {
            filterList.splice(filterColIndex, 1);
            setFilteredColumns(filterList);
        }

        setIsPanelVisible(false);
        setClickedHeader({});

        if (filterColIndex > -1) {
            getData(0, false, { sortList: null, filterList });
        }
    };

    const clearAllFilters = (isMainFilterEvent) => {
        setSortedColumns([]);
        setFilteredColumns([]);

        // Avoiding additional api call.
        if (!isMainFilterEvent) {
            getData(0, false);
        }
    };

    const closePanel = () => {
        setIsPanelVisible(false);
        setClickedHeader({});
    };

    /**
     * This will fix alignment issue in table body due to scrollbar width
     * @returns {Number} Padding right in pixels.
     */
    const calculatePadding = () => {
        if (!tableHeadRef.current || !tableBodyRef.current) {
            return 0;
        }

        const theadWidth = tableHeadRef.current.clientWidth;
        const tbodyWidth = tableBodyRef.current.clientWidth;
        return theadWidth - tbodyWidth;
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.clearFilters} style={topCss}>
                <ClearFilters onClick={() => clearAllFilters()} />
            </div>
            {isPanelVisible && (
                <OptionPanel
                    style={panelStyles}
                    header={getHeaderData()}
                    updateSortFilters={updateSortFilters}
                    clearFilter={clearColumnFilter}
                    closePanel={closePanel}
                />
            )}
            <table ref={tableRef}>
                <thead ref={tableHeadRef}>
                    <tr style={{ paddingRight: calculatePadding() }}>
                        <th style={{ width: '4%' }}>#</th>
                        {columns.map((h) => (
                            <th
                                onClick={(e) => onHeaderClick(e, h)}
                                key={`th-${h.i}`}
                                style={{ width: h.w }}
                                className={
                                    clickedHeader.th === h.th
                                        ? styles.selected
                                        : ''
                                }
                            >
                                {renderHeaderText(h)}

                                {renderSortArrow(h)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody ref={tableBodyRef} onScroll={onTableScroll}>
                    {tableData.map((c, i) => (
                        <tr key={i}>
                            <td style={{ width: '4%' }}>{i + 1}</td>
                            {columns.map((r) => (
                                <td
                                    key={`td-${i}-${r.i}`}
                                    style={{ width: r.w }}
                                >
                                    {r.subType === TYPES.currency
                                        ? currencyFormatter(c[r.prop])
                                        : c[r.prop]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {tableData.length === 0 && (
                <div className={styles.noRecords}>No Results</div>
            )}
        </div>
    );
};

InfiniteTable.propTypes = {
    zone: PropTypes.number,
    market: PropTypes.number,
    team: PropTypes.number,
    rm: PropTypes.number,
    start: PropTypes.shape({
        monthNum: PropTypes.number,
        epoch: PropTypes.number
    }),
    end: PropTypes.shape({
        monthNum: PropTypes.number,
        epoch: PropTypes.number
    }),
    allValues: PropTypes.shape({
        market: PropTypes.shape({
            value: PropTypes.number
        }),
        team: PropTypes.shape({
            value: PropTypes.number
        }),
        rm: PropTypes.shape({
            value: PropTypes.number
        })
    }),
    columns: PropTypes.array,
    scenario: PropTypes.string,
    topCss: PropTypes.shape({})
};

export default InfiniteTable;
